import * as React from 'react';
import { Popper, Paper, ClickAwayListener } from '@mui/material';
import { IconButton } from 'MaterialUIComponents';
import { faEllipsisH, faEllipsisV, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const styles = require('./EllipsisPopperMenu.scss') as {
    iconButton: string;
    toolTipButton: string;
    ellipseIcon: string;
    iconButtonActive: string;
    ellipseIconActive: string;
    ellipsisAlign: string;
    encIconButton: string;
    encIconButtonActive: string;
    encEllipsisIcon: string;
};

// ---- tiny global bus (keeps only one open without parent state) ----
const CHANNEL = 'vp:ellipsis-menu:open';
const bus: EventTarget = ((globalThis as any).__VP_MENU_BUS__ ??= new EventTarget());

export type EllipsisItem = {
    key: string | number;
    text: string;
    onClick: () => void;
    isDisabled?: boolean;
    isHidden?: boolean;
};

export interface EllipsisPopperMenuProps {
    items: EllipsisItem[];
    groupKey?: string;
    isEncompass?: boolean;
}

export const EllipsisPopperMenu: React.FC<EllipsisPopperMenuProps> = ({ items, groupKey = 'default', isEncompass }) => {
    const idRef = React.useRef(Symbol());
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    React.useEffect(() => {
        if (!open) return;
        bus.dispatchEvent(new CustomEvent(CHANNEL, { detail: { openerId: idRef.current, group: groupKey } }));
    }, [open, groupKey]);

    React.useEffect(() => {
        const onOpen = (e: Event) => {
            const { openerId, group } = (e as CustomEvent<{ openerId: symbol; group: string }>).detail || {};
            if (group !== groupKey || openerId === idRef.current) return;

            // Defer the state update so it doesn't happen during another component's render
            (typeof queueMicrotask === 'function' ? queueMicrotask : (fn: () => void) => setTimeout(fn, 0))(() => setAnchorEl(null));
        };

        bus.addEventListener(CHANNEL, onOpen as EventListener);
        return () => bus.removeEventListener(CHANNEL, onOpen as EventListener);
    }, [groupKey]); // note: no `open` dep; we don't need it here.

    React.useLayoutEffect(() => {
        if (open && anchorEl && !(anchorEl as any).isConnected) setAnchorEl(null);
    }, [open, anchorEl]);

    const visibleItems = React.useMemo(() => items.filter((i) => i.isHidden !== true), [items]);

    const toggleFrom = (el: HTMLElement) => {
        setAnchorEl((prev) => (prev === el ? null : el));
    };

    const close = () => setAnchorEl(null);

    const icon = open ? (
        <FontAwesomeIcon className={styles.ellipseIconActive} icon={faXmark} style={{ color: 'white' }} />
    ) : (
        <FontAwesomeIcon
            className={isEncompass ? styles.encEllipsisIcon : styles.ellipseIcon}
            icon={isEncompass ? faEllipsisH : faEllipsisV}
        />
    );

    return (
        <div>
            <IconButton
                className={
                    open
                        ? isEncompass
                            ? styles.encIconButtonActive
                            : styles.iconButtonActive
                        : isEncompass
                          ? styles.encIconButton
                          : styles.iconButton
                }
                onMouseDown={(e) => e.stopPropagation()} // avoid row handlers
                onClick={(e) => {
                    e.stopPropagation();
                    toggleFrom(e.currentTarget as HTMLElement);
                }}
                disableRipple
                disableFocusRipple
                size="small"
                aria-haspopup="menu"
                aria-expanded={open ? 'true' : undefined}
            >
                {icon}
            </IconButton>

            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="left-start"
                modifiers={[
                    { name: 'flip', options: { fallbackPlacements: ['right-start', 'bottom-start', 'top-start'] } },
                    { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
                ]}
                disablePortal={false}
                sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
            >
                <ClickAwayListener onClickAway={close} touchEvent="onTouchStart">
                    <Paper
                        elevation={1}
                        role="menu"
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            p: '4px 0',
                            borderRadius: '4px',
                            boxShadow: '0 0 15px 0 rgba(0, 0, 0, 0.20), 0 4px 6px 4px rgba(0, 0, 0, 0.05)',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {visibleItems.map((opt) => (
                            <div
                                key={opt.key}
                                className={styles.toolTipButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!opt.isDisabled) opt.onClick();
                                    close();
                                }}
                                style={{
                                    ...(opt.isDisabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined),
                                    alignItems: 'flex-start',
                                    alignSelf: 'stretch',
                                }}
                                role="menuitem"
                            >
                                {opt.text}
                            </div>
                        ))}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </div>
    );
};
